/* =============================================================================
   premium-video-player.js
   Native HTML5 + HLS.js premium tutorial player for CloudFront protected HLS.

   Frontend requirements:
   - CloudFront must be exposed on a first-party hostname such as
     https://video.cottonwoolshop.com
   - Credentialed CORS must explicitly allow the Shopify storefront origin(s)
     and Access-Control-Allow-Credentials: true
   - The backend must issue CloudFront signed cookies before the player loads
     the stable HLS manifest URL
   ============================================================================= */

(function () {
  "use strict";

  if (
    window.customElements &&
    window.customElements.get("premium-video-player")
  ) {
    return;
  }

  const metadataCache = new Map();
  let hlsScriptPromise = null;

  function parseBool(value) {
    return (
      value === true || value === "true" || value === "1" || value === "yes"
    );
  }

  function escapeHtml(value) {
    const node = document.createElement("div");
    node.appendChild(document.createTextNode(String(value ?? "")));
    return node.innerHTML;
  }

  function formatClock(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
    const total = Math.floor(seconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    if (hours > 0) {
      return [hours, minutes, secs]
        .map((part, index) =>
          index === 0 ? String(part) : String(part).padStart(2, "0"),
        )
        .join(":");
    }
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }

  function resolveUrl(baseUrl, maybeRelativeUrl) {
    if (!maybeRelativeUrl) return "";
    try {
      return new URL(maybeRelativeUrl, baseUrl).toString();
    } catch (_error) {
      return maybeRelativeUrl;
    }
  }

  function canUseNativeHls(video) {
    return Boolean(video && video.canPlayType("application/vnd.apple.mpegurl"));
  }

  async function ensureHlsScript(src) {
    if (window.Hls) return window.Hls;
    if (!src) throw new Error("Missing hls.js asset URL");
    if (!hlsScriptPromise) {
      hlsScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => resolve(window.Hls);
        script.onerror = () => reject(new Error("Failed to load hls.js asset"));
        document.head.appendChild(script);
      });
    }
    return hlsScriptPromise;
  }

  class PremiumVideoPlayer extends HTMLElement {
    constructor() {
      super();
      this._connected = false;
      this._videoData = null;
      this._metadata = null;
      this._hls = null;
      this._qualityOptions = [];
      this._resumePoint = null;
      this._lastPersistedSecond = 0;
      this._activeChapterIndex = -1;
      this._chapterFrame = 0;
      this._hasRetriedSession = false;
      this._restoreAfterReload = null;
      this._statusState = "idle";

      this._onVideoLoadedMetadata = this._onVideoLoadedMetadata.bind(this);
      this._onVideoCanPlay = this._onVideoCanPlay.bind(this);
      this._onVideoTimeUpdate = this._onVideoTimeUpdate.bind(this);
      this._onVideoPause = this._onVideoPause.bind(this);
      this._onVideoEnded = this._onVideoEnded.bind(this);
      this._onVideoError = this._onVideoError.bind(this);
      this._onVisibilityChange = this._onVisibilityChange.bind(this);
      this._onPageHide = this._onPageHide.bind(this);
      this._onSpeedChange = this._onSpeedChange.bind(this);
      this._onQualityChange = this._onQualityChange.bind(this);
      this._onOverlayClick = this._onOverlayClick.bind(this);
      this._onDismissClick = this._onDismissClick.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._render();
      this._cacheElements();
      this._bindEvents();
      this._applyStaticContent();

      if (this.videoId) {
        this.start();
      } else {
        this._setState("idle", {
          title: this.showTitle
            ? this.videoTitle || "Premium tutorial"
            : "Premium tutorial",
          message: "Choose a purchased tutorial to begin watching.",
        });
      }
    }

    disconnectedCallback() {
      this._persistProgress();
      this._unbindEvents();
      this._destroyHls();
      if (this._chapterFrame) {
        window.cancelAnimationFrame(this._chapterFrame);
        this._chapterFrame = 0;
      }
      this._connected = false;
    }

    get accessUrl() {
      return this.getAttribute("access-url") || "/apps/videos/access";
    }

    get loginUrl() {
      return this.getAttribute("login-url") || "/account/login";
    }

    get hlsScriptUrl() {
      return this.getAttribute("hls-script-url") || "";
    }

    get customerLoggedIn() {
      return parseBool(this.getAttribute("customer-logged-in"));
    }

    get showTitle() {
      return parseBool(this.getAttribute("show-title") ?? "true");
    }

    get showChapters() {
      return parseBool(this.getAttribute("show-chapters") ?? "true");
    }

    get dismissible() {
      return parseBool(this.getAttribute("dismissible"));
    }

    get videoId() {
      return this.getAttribute("video-id") || "";
    }

    get videoTitle() {
      return this.getAttribute("video-title") || "";
    }

    get descriptionText() {
      return this.getAttribute("description") || "";
    }

    get purchaseCtaLabel() {
      return this.getAttribute("purchase-cta-label") || "Purchase tutorial";
    }

    get purchaseCtaUrl() {
      return this.getAttribute("purchase-cta-url") || "";
    }

    get publicPosterUrl() {
      return this.getAttribute("public-poster-url") || "";
    }

    async loadVideo(config) {
      if (config?.id) this.setAttribute("video-id", config.id);
      if (config?.title) this.setAttribute("video-title", config.title);
      if (config?.description)
        this.setAttribute("description", config.description);
      if (config?.purchaseUrl)
        this.setAttribute("purchase-cta-url", config.purchaseUrl);
      if (config?.purchaseLabel)
        this.setAttribute("purchase-cta-label", config.purchaseLabel);
      this.removeAttribute("hidden");
      this._applyStaticContent();
      await this.start();
    }

    async start() {
      this._destroyHls();
      this._resetTransientState();
      this._applyStaticContent();

      if (!this.videoId) {
        this._setState("idle", {
          title: "Premium tutorial",
          message: "Add a video ID to load this tutorial.",
        });
        return;
      }

      if (!this.customerLoggedIn) {
        this._setState("not-logged-in", {
          title: "Please sign in to watch this tutorial.",
          message:
            "Sign in to your Cottonwool Creations account to verify access and start streaming.",
          actions: [
            {
              type: "link",
              label: "Sign In",
              href: this.loginUrl,
              variant: "primary",
            },
          ],
        });
        return;
      }

      this._setState("loading-access", {
        title: "Checking your access…",
        message:
          "We’re verifying your purchase and preparing a secure viewing session.",
        busy: true,
      });

      try {
        const auth = await this._authorise();
        if (auth.status === 401) {
          this._setState("not-logged-in", {
            title: "Please sign in to watch this tutorial.",
            message:
              "Sign in to your Cottonwool Creations account to continue.",
            actions: [
              {
                type: "link",
                label: "Sign In",
                href: this.loginUrl,
                variant: "primary",
              },
            ],
          });
          return;
        }

        if (auth.status === 403) {
          this._setState("not-purchased", {
            title: "This tutorial is available after purchase.",
            message: `This tutorial is available after purchasing ${this.videoTitle || "this course"}.`,
            actions: this.purchaseCtaUrl
              ? [
                  {
                    type: "link",
                    label: this.purchaseCtaLabel,
                    href: this.purchaseCtaUrl,
                    variant: "primary",
                  },
                ]
              : [],
          });
          return;
        }

        this._videoData = auth.body.video;

        if (this._videoData?.contentType === "download") {
          this._destroyHls();
          if (this._els.video) {
            this._els.video.pause();
            this._els.video.removeAttribute("src");
            this._els.video.load();
          }
          this._toggleChapters(false);
          if (this._els.qualityField) this._els.qualityField.hidden = true;
          if (this._els.meta) this._els.meta.hidden = true;
          this._setState("download-only", {
            title: "Download ready",
            message:
              "This purchase is a downloadable resource. Use the download buttons below.",
          });
          return;
        }

        this._setState("loading-video", {
          title: "Authorised — preparing your tutorial…",
          message: "Loading the stream, poster and chapter information.",
          busy: true,
        });

        await this._loadMetadata();
        await this._attachPlayback();
      } catch (error) {
        console.error(
          "[cottonwool-video] Failed to initialise premium player",
          error,
        );
        this._setState("network-error", {
          title: "We couldn’t prepare this tutorial.",
          message: "Please try again in a moment.",
          actions: [
            {
              type: "button",
              action: "retry",
              label: "Retry",
              variant: "primary",
            },
          ],
        });
      }
    }

    async _authorise() {
      const response = await fetch(
        `${this.accessUrl}?video=${encodeURIComponent(this.videoId)}`,
        {
          credentials: "same-origin",
          headers: { accept: "application/json" },
        },
      );

      if (response.status === 401 || response.status === 403) {
        return { status: response.status };
      }

      if (!response.ok) {
        throw new Error(`Access request failed: ${response.status}`);
      }

      const body = await response.json();

      this._applySessionCookies(body?.cookies || body?.video?.cookies);

      // Backward compatibility:
      // Legacy backend contract returned { playbackUrl, expiresAt, videoId }
      // while the new HLS contract returns { authorised, video: {...} }.
      if (body?.video) {
        return { status: 200, body };
      }

      if (body?.playbackUrl) {
        return {
          status: 200,
          body: {
            authorised: true,
            video: {
              id: body.videoId || this.videoId,
              title: this.videoTitle || "Premium tutorial",
              playbackUrl: body.playbackUrl,
              baseUrl: "",
              manifestUrl: "",
              metadataUrl: "",
            },
          },
        };
      }

      throw new Error("Access response missing video playback information");
    }

    _applySessionCookies(cookieStrings) {
      if (!Array.isArray(cookieStrings) || cookieStrings.length === 0) return;

      cookieStrings.forEach((rawCookie) => {
        if (typeof rawCookie !== "string" || !rawCookie.trim()) return;

        // document.cookie cannot set HttpOnly; ignore it if backend includes it.
        const safeCookie = rawCookie
          .split(";")
          .map((part) => part.trim())
          .filter((part) => part && part.toLowerCase() !== "httponly")
          .join("; ");

        if (!safeCookie) return;

        try {
          document.cookie = safeCookie;
        } catch (error) {
          console.warn(
            "[cottonwool-video] Failed to set signed session cookie",
            error,
          );
        }
      });
    }

    async _loadMetadata() {
      this._metadata = null;
      this._toggleChapters(false);

      if (!this._videoData?.metadataUrl) return;

      try {
        let metadataPromise = metadataCache.get(this._videoData.metadataUrl);
        if (!metadataPromise) {
          metadataPromise = fetch(this._videoData.metadataUrl, {
            credentials: "include",
            headers: { accept: "application/json" },
          }).then((response) => {
            if (!response.ok) {
              throw new Error(`Metadata request failed: ${response.status}`);
            }
            return response.json();
          });
          metadataCache.set(this._videoData.metadataUrl, metadataPromise);
        }

        const metadata = await metadataPromise;
        this._metadata = metadata;
      } catch (error) {
        console.warn(
          "[cottonwool-video] Metadata unavailable; chapters hidden",
          error,
        );
        this._metadata = null;
      }
    }

    async _attachPlayback() {
      const video = this._els.video;
      if (!video || !this._videoData) return;

      // Legacy direct playback URL support (non-HLS contract).
      if (this._videoData.playbackUrl) {
        this._toggleChapters(false);
        video.preload = "metadata";
        video.setAttribute("crossorigin", "use-credentials");
        video.src = this._videoData.playbackUrl;
        video.load();
        return;
      }

      const metadata = this._metadata;
      const manifestUrl = metadata?.hls
        ? resolveUrl(this._videoData.baseUrl, metadata.hls)
        : this._videoData.manifestUrl;

      if (!manifestUrl) {
        throw new Error("Missing manifest URL for HLS playback");
      }
      const posterUrl =
        this.publicPosterUrl ||
        (metadata?.poster
          ? resolveUrl(this._videoData.baseUrl, metadata.poster)
          : this._videoData.posterUrl);

      if (posterUrl) {
        video.poster = posterUrl;
      }

      this._resumePoint = this._readProgress();
      this._renderResumeControls();
      this._renderMetadataFields();
      this._renderChapters();

      video.preload = "metadata";
      video.setAttribute("crossorigin", "use-credentials");

      if (canUseNativeHls(video)) {
        video.src = manifestUrl;
        video.load();
        return;
      }

      const HlsCtor = await ensureHlsScript(this.hlsScriptUrl);
      if (!HlsCtor || !HlsCtor.isSupported()) {
        throw new Error("HLS playback is not supported in this browser");
      }

      const hls = new HlsCtor({
        enableWorker: true,
        startLevel: -1,
        backBufferLength: 90,
        xhrSetup(xhr) {
          xhr.withCredentials = true;
        },
      });

      this._hls = hls;
      hls.attachMedia(video);
      hls.on(HlsCtor.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(manifestUrl);
      });
      hls.on(HlsCtor.Events.MANIFEST_PARSED, (_event, data) => {
        this._populateQualityOptions(data?.levels ?? []);
      });
      hls.on(HlsCtor.Events.LEVEL_SWITCHED, (_event, data) => {
        if (this._els.qualitySelect && data?.level !== undefined) {
          this._els.qualitySelect.value = String(data.level);
        }
      });
      hls.on(HlsCtor.Events.ERROR, async (_event, data) => {
        if (!data) return;

        if (data.fatal && data.type === HlsCtor.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
          return;
        }

        const responseCode = data?.response?.code;
        const isLikelyExpired = responseCode === 403;

        if (isLikelyExpired) {
          const recovered = await this._refreshSessionAndResume();
          if (recovered) return;
        }

        if (data.fatal && data.type === HlsCtor.ErrorTypes.NETWORK_ERROR) {
          this._setState("session-expired", {
            title: "Your viewing session has expired.",
            message: "Please refresh your access to continue.",
            actions: [
              {
                type: "button",
                action: "retry",
                label: "Retry",
                variant: "primary",
              },
            ],
          });
          return;
        }

        if (data.fatal) {
          this._setState("playback-error", {
            title: "Playback ran into a problem.",
            message: "Please refresh your access and try again.",
            actions: [
              {
                type: "button",
                action: "retry",
                label: "Retry",
                variant: "primary",
              },
            ],
          });
        }
      });
    }

    async _refreshSessionAndResume() {
      if (this._hasRetriedSession || !this.videoId) {
        return false;
      }

      this._hasRetriedSession = true;
      const video = this._els.video;
      const time = video?.currentTime || 0;
      const shouldResumePlaying = Boolean(
        video && !video.paused && !video.ended,
      );
      if (video) video.pause();

      this._setState("loading-video", {
        title: "Refreshing your secure viewing session…",
        message: "Please hold on while we renew your access.",
        busy: true,
      });

      try {
        const auth = await this._authorise();
        if (auth.status !== 200) {
          this._setState("session-expired", {
            title: "Your viewing session has expired.",
            message: "Please refresh your access to continue.",
            actions: [
              {
                type: "button",
                action: "retry",
                label: "Retry",
                variant: "primary",
              },
            ],
          });
          return false;
        }

        this._videoData = auth.body.video;
        this._restoreAfterReload = {
          time,
          autoplay: shouldResumePlaying,
        };
        await this._attachPlayback();
        return true;
      } catch (error) {
        console.error(
          "[cottonwool-video] Failed to refresh viewing session",
          error,
        );
        this._setState("session-expired", {
          title: "Your viewing session has expired.",
          message: "Please refresh your access to continue.",
          actions: [
            {
              type: "button",
              action: "retry",
              label: "Retry",
              variant: "primary",
            },
          ],
        });
        return false;
      }
    }

    _populateQualityOptions(levels) {
      const select = this._els.qualitySelect;
      const wrap = this._els.qualityField;
      if (!select || !wrap) return;

      const byHeight = new Map();
      levels.forEach((level, index) => {
        if (typeof level?.height === "number" && !byHeight.has(level.height)) {
          byHeight.set(level.height, index);
        }
      });

      const options = [{ value: "auto", label: "Auto" }].concat(
        Array.from(byHeight.entries())
          .sort((a, b) => b[0] - a[0])
          .map(([height, index]) => ({
            value: String(index),
            label: `${height}p`,
          })),
      );

      this._qualityOptions = options;
      select.innerHTML = options
        .map(
          (option) =>
            `<option value="${escapeHtml(option.value)}">${escapeHtml(option.label)}</option>`,
        )
        .join("");
      select.value = "auto";
      wrap.hidden = options.length <= 1;
    }

    _renderChapters() {
      const wrap = this._els.chaptersWrap;
      const list = this._els.chapters;
      if (!wrap || !list) return;

      const chapters = Array.isArray(this._metadata?.chapters)
        ? this._metadata.chapters
        : [];
      if (!this.showChapters || chapters.length === 0) {
        list.innerHTML = "";
        this._toggleChapters(false);
        return;
      }

      list.innerHTML = chapters
        .map((chapter, index) => {
          const thumbUrl = chapter.thumbnail
            ? resolveUrl(this._videoData.baseUrl, chapter.thumbnail)
            : "";
          const title = chapter.title || `Chapter ${index + 1}`;
          return `
            <button class="cw-video__chapter cw-video__chapter--pending" type="button" data-chapter-index="${index}" data-start="${Number(chapter.start) || 0}">
              <span class="cw-video__chapter-thumb${thumbUrl ? "" : " cw-video__chapter-thumb--fallback"}">
                ${
                  thumbUrl
                    ? `<img src="${escapeHtml(thumbUrl)}" alt="" loading="lazy" decoding="async">`
                    : `<span>Chapter</span>`
                }
              </span>
              <span class="cw-video__chapter-copy">
                <span class="cw-video__chapter-kicker"><span class="cw-video__chapter-status" aria-hidden="true"></span>${escapeHtml(`${chapter.number || index + 1}. Chapter`)}</span>
                <span class="cw-video__chapter-title">${escapeHtml(title)}</span>
              </span>
              <span class="cw-video__chapter-time">${escapeHtml(formatClock(Number(chapter.start) || 0))}</span>
            </button>
          `;
        })
        .join("");

      list.querySelectorAll("img").forEach((img) => {
        img.addEventListener(
          "error",
          () => {
            const thumb = img.closest(".cw-video__chapter-thumb");
            if (!thumb) return;
            thumb.innerHTML = "<span>Chapter</span>";
            thumb.classList.add("cw-video__chapter-thumb--fallback");
          },
          { once: true },
        );
      });

      this._toggleChapters(true);
    }

    _toggleChapters(visible) {
      if (!this._els.layout || !this._els.chaptersWrap) return;
      this._els.chaptersWrap.hidden = !visible;
      this._els.layout.classList.toggle(
        "cw-video__layout--with-chapters",
        visible,
      );
    }

    _renderResumeControls() {
      const wrap = this._els.resume;
      if (!wrap || !this._els.resumeNote || !this._els.resumeActions) return;

      if (!this._resumePoint) {
        wrap.hidden = true;
        this._els.resumeActions.innerHTML = "";
        this._els.resumeNote.textContent = "";
        return;
      }

      wrap.hidden = false;
      this._els.resumeNote.innerHTML = `Continue watching <strong>${escapeHtml(formatClock(this._resumePoint.currentTime))}</strong>`;
      this._els.resumeActions.innerHTML = `
        <button type="button" class="cw-btn cw-btn-primary" data-action="continue">Continue watching</button>
        <button type="button" class="cw-btn cw-btn-outline" data-action="restart">Start from beginning</button>
      `;
    }

    _renderMetadataFields() {
      if (this._els.speedSelect) {
        this._els.speedSelect.value = String(this._els.video.playbackRate || 1);
      }
      if (this._els.meta) {
        this._els.meta.hidden = false;
      }
    }

    _applyStaticContent() {
      if (!this._els.heading || !this._els.description || !this._els.intro)
        return;
      const title =
        this.videoTitle || this._videoData?.title || "Premium tutorial";
      const description =
        this.descriptionText || this._videoData?.description || "";
      this._els.heading.textContent = title;
      this._els.description.textContent = description;
      this._els.intro.hidden = !this.showTitle;
      this._els.description.hidden = !description || !this.showTitle;
      this._els.dismiss.hidden = !this.dismissible;
    }

    _setState(state, options = {}) {
      this._statusState = state;
      const overlay = this._els.overlay;
      if (!overlay) return;

      const isReady = state === "ready";
      overlay.hidden = isReady;
      this._els.spinner.hidden = !options.busy;
      this._els.overlayTitle.textContent = options.title || "";
      this._els.overlayText.textContent = options.message || "";
      this._els.live.textContent = options.message || options.title || "";
      this._els.overlayActions.innerHTML = "";

      (options.actions || []).forEach((action) => {
        let node;
        const variantClass =
          action.variant === "outline"
            ? "cw-btn-outline-white"
            : "cw-btn-primary";
        if (action.type === "link") {
          node = document.createElement("a");
          node.href = action.href;
          node.className = `cw-btn ${variantClass}`;
          node.textContent = action.label;
        } else {
          node = document.createElement("button");
          node.type = "button";
          node.className = `cw-btn ${variantClass}`;
          node.dataset.action = action.action;
          node.textContent = action.label;
        }
        this._els.overlayActions.appendChild(node);
      });

      this.dispatchEvent(
        new CustomEvent("premium-video-player:state-change", {
          bubbles: true,
          detail: {
            state,
            videoId: this.videoId || "",
            entitled: state === "ready" || state === "download-only",
            assets: Array.isArray(this._videoData?.assets)
              ? this._videoData.assets
                  .map((asset) => {
                    if (!asset || typeof asset !== "object") return null;
                    const url =
                      typeof asset.url === "string"
                        ? asset.url
                        : typeof asset.href === "string"
                          ? asset.href
                          : "";
                    if (!url) return null;
                    return {
                      url,
                      label:
                        asset.label ||
                        asset.title ||
                        asset.name ||
                        "Download PDF",
                    };
                  })
                  .filter(Boolean)
              : [],
          },
        }),
      );
    }

    _readProgress() {
      try {
        const raw = window.localStorage.getItem(this._progressKey());
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (
          !parsed ||
          !Number.isFinite(parsed.currentTime) ||
          parsed.currentTime < 10
        ) {
          return null;
        }
        return parsed;
      } catch (_error) {
        return null;
      }
    }

    _persistProgress(force = false) {
      const video = this._els.video;
      if (!video || !this.videoId || !Number.isFinite(video.currentTime))
        return;
      const currentTime = video.currentTime;
      const duration = video.duration;

      if (!force && Math.abs(currentTime - this._lastPersistedSecond) < 10) {
        return;
      }

      if (currentTime < 10) {
        return;
      }

      if (
        Number.isFinite(duration) &&
        duration > 0 &&
        duration - currentTime <= 30
      ) {
        window.localStorage.removeItem(this._progressKey());
        this._lastPersistedSecond = 0;
        return;
      }

      this._lastPersistedSecond = currentTime;
      window.localStorage.setItem(
        this._progressKey(),
        JSON.stringify({
          currentTime,
          duration: Number.isFinite(duration) ? duration : null,
          updatedAt: new Date().toISOString(),
        }),
      );
    }

    _clearProgress() {
      window.localStorage.removeItem(this._progressKey());
      this._resumePoint = null;
      this._lastPersistedSecond = 0;
      this._renderResumeControls();
    }

    _progressKey() {
      return `cottonwool:video:${this.videoId}:progress`;
    }

    _resetTransientState() {
      this._hasRetriedSession = false;
      this._restoreAfterReload = null;
      this._activeChapterIndex = -1;
      this._lastPersistedSecond = 0;
      this._metadata = null;
      this._videoData =
        this._videoData && this._videoData.id === this.videoId
          ? this._videoData
          : null;
      if (this._els.video) {
        this._els.video.pause();
        this._els.video.removeAttribute("src");
        this._els.video.load();
      }
      if (this._els.qualityField) this._els.qualityField.hidden = true;
      if (this._els.chapters) this._els.chapters.innerHTML = "";
      this._toggleChapters(false);
    }

    _onVideoLoadedMetadata() {
      const video = this._els.video;
      if (!video) return;
      if (
        this._restoreAfterReload &&
        Number.isFinite(this._restoreAfterReload.time)
      ) {
        video.currentTime = this._restoreAfterReload.time;
        if (this._restoreAfterReload.autoplay) {
          video.play().catch(() => {});
        }
        this._restoreAfterReload = null;
      }
    }

    _onVideoCanPlay() {
      this._setState("ready", {});
      this._updateChapterState();
    }

    _onVideoTimeUpdate() {
      if (this._chapterFrame) return;
      this._chapterFrame = window.requestAnimationFrame(() => {
        this._chapterFrame = 0;
        this._updateChapterState();
      });
      this._persistProgress(false);
    }

    _updateChapterState() {
      const chapters = Array.isArray(this._metadata?.chapters)
        ? this._metadata.chapters
        : [];
      const video = this._els.video;
      const buttons =
        this._els.chapters?.querySelectorAll(".cw-video__chapter") || [];
      if (!video || chapters.length === 0 || buttons.length === 0) return;

      const time = video.currentTime || 0;
      let activeIndex = -1;
      chapters.forEach((chapter, index) => {
        const start = Number(chapter.start) || 0;
        const end = Number(chapter.end) || Number.POSITIVE_INFINITY;
        if (time >= start && time < end) {
          activeIndex = index;
        }
      });

      if (
        activeIndex === -1 &&
        chapters.length &&
        time >= (Number(chapters[chapters.length - 1].start) || 0)
      ) {
        activeIndex = chapters.length - 1;
      }

      if (activeIndex === this._activeChapterIndex) {
        return;
      }

      this._activeChapterIndex = activeIndex;
      buttons.forEach((button, index) => {
        button.classList.remove(
          "cw-video__chapter--active",
          "cw-video__chapter--done",
          "cw-video__chapter--pending",
        );
        if (index < activeIndex) {
          button.classList.add("cw-video__chapter--done");
          button.removeAttribute("aria-current");
        } else if (index === activeIndex) {
          button.classList.add("cw-video__chapter--active");
          button.setAttribute("aria-current", "true");
        } else {
          button.classList.add("cw-video__chapter--pending");
          button.removeAttribute("aria-current");
        }
      });
    }

    _onVideoPause() {
      this._persistProgress(true);
    }

    _onVideoEnded() {
      this._clearProgress();
    }

    async _onVideoError() {
      if (this._statusState === "loading-video") return;
      const recovered = await this._refreshSessionAndResume();
      if (!recovered) {
        this._setState("playback-error", {
          title: "Playback ran into a problem.",
          message: "Please refresh your access and try again.",
          actions: [
            {
              type: "button",
              action: "retry",
              label: "Retry",
              variant: "primary",
            },
          ],
        });
      }
    }

    _onVisibilityChange() {
      if (document.visibilityState === "hidden") {
        this._persistProgress(true);
      }
    }

    _onPageHide() {
      this._persistProgress(true);
    }

    _onSpeedChange() {
      const nextRate = Number(this._els.speedSelect?.value || 1);
      if (Number.isFinite(nextRate) && this._els.video) {
        this._els.video.playbackRate = nextRate;
      }
    }

    _onQualityChange() {
      if (!this._hls || !this._els.qualitySelect) return;
      const value = this._els.qualitySelect.value;
      this._hls.currentLevel = value === "auto" ? -1 : Number(value);
    }

    _onOverlayClick(event) {
      const button = event.target.closest("[data-action]");
      if (!button) return;
      const action = button.dataset.action;
      if (action === "retry") {
        this.start();
      }
      if (action === "continue") {
        if (this._resumePoint && this._els.video) {
          this._els.video.currentTime = this._resumePoint.currentTime;
          this._els.video.play().catch(() => {});
          this._setState("ready", {});
        }
      }
      if (action === "restart") {
        if (this._els.video) {
          this._clearProgress();
          this._els.video.currentTime = 0;
          this._els.video.play().catch(() => {});
          this._setState("ready", {});
        }
      }
    }

    _onDismissClick() {
      this._persistProgress(true);
      if (this._els.video) {
        this._els.video.pause();
      }
      this.setAttribute("hidden", "");
      this.dispatchEvent(
        new CustomEvent("premium-video-player:closed", { bubbles: true }),
      );
    }

    _bindEvents() {
      this._els.video.addEventListener(
        "loadedmetadata",
        this._onVideoLoadedMetadata,
      );
      this._els.video.addEventListener("canplay", this._onVideoCanPlay);
      this._els.video.addEventListener("timeupdate", this._onVideoTimeUpdate);
      this._els.video.addEventListener("pause", this._onVideoPause);
      this._els.video.addEventListener("ended", this._onVideoEnded);
      this._els.video.addEventListener("error", this._onVideoError);
      this._els.speedSelect.addEventListener("change", this._onSpeedChange);
      this._els.qualitySelect.addEventListener("change", this._onQualityChange);
      this._els.overlayActions.addEventListener("click", this._onOverlayClick);
      this._els.resumeActions.addEventListener("click", this._onOverlayClick);
      this._els.chapters.addEventListener("click", (event) => {
        const button = event.target.closest("[data-chapter-index]");
        if (!button || !this._els.video) return;
        const nextTime = Number(button.dataset.start || 0);
        this._els.video.currentTime = nextTime;
        this._els.video.play().catch(() => {});
      });
      this._els.dismiss.addEventListener("click", this._onDismissClick);
      document.addEventListener("visibilitychange", this._onVisibilityChange);
      window.addEventListener("pagehide", this._onPageHide);
    }

    _unbindEvents() {
      if (!this._els?.video) return;
      this._els.video.removeEventListener(
        "loadedmetadata",
        this._onVideoLoadedMetadata,
      );
      this._els.video.removeEventListener("canplay", this._onVideoCanPlay);
      this._els.video.removeEventListener(
        "timeupdate",
        this._onVideoTimeUpdate,
      );
      this._els.video.removeEventListener("pause", this._onVideoPause);
      this._els.video.removeEventListener("ended", this._onVideoEnded);
      this._els.video.removeEventListener("error", this._onVideoError);
      this._els.speedSelect?.removeEventListener("change", this._onSpeedChange);
      this._els.qualitySelect?.removeEventListener(
        "change",
        this._onQualityChange,
      );
      this._els.overlayActions?.removeEventListener(
        "click",
        this._onOverlayClick,
      );
      this._els.resumeActions?.removeEventListener(
        "click",
        this._onOverlayClick,
      );
      this._els.dismiss?.removeEventListener("click", this._onDismissClick);
      document.removeEventListener(
        "visibilitychange",
        this._onVisibilityChange,
      );
      window.removeEventListener("pagehide", this._onPageHide);
    }

    _destroyHls() {
      if (this._hls) {
        this._hls.destroy();
        this._hls = null;
      }
    }

    _render() {
      this.innerHTML = `
        <div class="cw-video__surface">
          <div class="cw-video__intro">
            <div class="cw-video__intro-copy" data-role="intro">
              <p class="cw-video__eyebrow">Members tutorial</p>
              <h2 class="cw-video__heading"></h2>
              <p class="cw-video__description"></p>
            </div>
            <button type="button" class="cw-btn cw-btn-outline cw-video__dismiss" hidden>Hide player</button>
          </div>
          <div class="cw-video__layout">
            <div class="cw-video__main">
              <div class="cw-video__frame">
                <video class="cw-video__media" controls playsinline preload="metadata" controlsList="nodownload"></video>
                <div class="cw-video__overlay">
                  <div class="cw-video__overlay-card">
                    <div class="cw-video__spinner" hidden></div>
                    <h3 class="cw-video__overlay-title"></h3>
                    <p class="cw-video__overlay-text"></p>
                    <div class="cw-video__overlay-actions"></div>
                  </div>
                </div>
                <span class="cw-video__assistive" aria-live="polite"></span>
              </div>
              <div class="cw-video__toolbar">
                <div class="cw-video__resume" hidden>
                  <span class="cw-video__resume-note"></span>
                  <div class="cw-video__resume-actions"></div>
                </div>
                <div class="cw-video__meta" hidden>
                  <div class="cw-video__field cw-video__quality" hidden>
                    <label for="cw-video-quality-${this.id || "player"}">Quality</label>
                    <select id="cw-video-quality-${this.id || "player"}" data-role="quality-select">
                      <option value="auto">Auto</option>
                    </select>
                  </div>
                  <div class="cw-video__field">
                    <label for="cw-video-speed-${this.id || "player"}">Speed</label>
                    <select id="cw-video-speed-${this.id || "player"}" data-role="speed-select">
                      <option value="0.75">0.75×</option>
                      <option value="1" selected>1×</option>
                      <option value="1.25">1.25×</option>
                      <option value="1.5">1.5×</option>
                      <option value="2">2×</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <aside class="cw-video__chapters-wrap" hidden>
              <h3 class="cw-video__chapters-heading">Chapters</h3>
              <div class="cw-video__chapters"></div>
            </aside>
          </div>
        </div>
      `;
    }

    _cacheElements() {
      this._els = {
        intro: this.querySelector("[data-role='intro']"),
        heading: this.querySelector(".cw-video__heading"),
        description: this.querySelector(".cw-video__description"),
        dismiss: this.querySelector(".cw-video__dismiss"),
        layout: this.querySelector(".cw-video__layout"),
        video: this.querySelector(".cw-video__media"),
        overlay: this.querySelector(".cw-video__overlay"),
        overlayTitle: this.querySelector(".cw-video__overlay-title"),
        overlayText: this.querySelector(".cw-video__overlay-text"),
        overlayActions: this.querySelector(".cw-video__overlay-actions"),
        spinner: this.querySelector(".cw-video__spinner"),
        live: this.querySelector(".cw-video__assistive"),
        resume: this.querySelector(".cw-video__resume"),
        resumeNote: this.querySelector(".cw-video__resume-note"),
        resumeActions: this.querySelector(".cw-video__resume-actions"),
        meta: this.querySelector(".cw-video__meta"),
        qualityField: this.querySelector(".cw-video__quality"),
        qualitySelect: this.querySelector('[data-role="quality-select"]'),
        speedSelect: this.querySelector('[data-role="speed-select"]'),
        chaptersWrap: this.querySelector(".cw-video__chapters-wrap"),
        chapters: this.querySelector(".cw-video__chapters"),
      };
    }
  }

  window.customElements.define("premium-video-player", PremiumVideoPlayer);
})();
